/**
 * Persistence strategy for guided-tour completion state.
 *
 * Today: localStorage-backed (per-device, per-browser). Tomorrow:
 * backend-backed via `PUT /onboarding/tours/{id}/completed` so the
 * customer's "seen this tour" state syncs across devices and matches
 * the existing per-user `onboarding_step` semantics.
 *
 * The `useGuidedTour` hook accepts a `TourPersistence` so the swap
 * is one line at the wiring site (SidebarTourMount). UI components
 * never touch storage directly.
 *
 * Ported from refactor-next-stable (commit 43822d61) — same shape,
 * same storage key, so a customer who saw the tour under the legacy
 * portal sees the v3 tour as already-completed (no replay).
 */

export type TourPersistence = {
  isCompleted: (tourId: string) => Promise<boolean>;
  markCompleted: (tourId: string) => Promise<void>;
  reset: (tourId: string) => Promise<void>;
};

const STORAGE_PREFIX = "rook-portal:tour:";
const COMPLETED_VALUE = "1";

const storageKey = (tourId: string): string => `${STORAGE_PREFIX}${tourId}`;

/**
 * Factory for the localStorage-backed persistence. Async signature
 * matches the future backend variant so the hook doesn't need to
 * branch on storage flavour.
 *
 * Failures are logged at warn — localStorage can throw on private
 * browsing, quota exceeded, or strict-CSP setups. We default the
 * read path to `false` (tour shows) so a customer who can't store
 * the bit still sees the tour at most once per session.
 */
export const localStorageTourPersistence = (): TourPersistence => ({
  isCompleted: async (tourId) => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(storageKey(tourId)) === COMPLETED_VALUE;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[tourPersistence] read failed for ${tourId}`, err);
      return false;
    }
  },
  markCompleted: async (tourId) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey(tourId), COMPLETED_VALUE);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[tourPersistence] write failed for ${tourId}`, err);
    }
  },
  reset: async (tourId) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(storageKey(tourId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[tourPersistence] reset failed for ${tourId}`, err);
    }
  },
});
